import React, { createContext, useContext, useMemo } from "react";
import { useAuthData } from "./AuthDataContext";
import { ROLES } from "../constants/roles";

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const { userData, loading } = useAuthData();

  // Derive all permission state from userData
  const permissions = userData?.permissions || [];
  const role = userData?.role_name?.toLowerCase() || null;
  const isSuperAdmin = userData?.isSuperAdmin || false;
  const hasOrganization = !!userData?.organisation_id;
  const allOrganisations = userData?.all_organisations || [];

  /**
   * Check if user has permission for a specific action
   * Super Admins always return true
   */
  const can = useMemo(() => (section, key, action) => {
    // Super Admin bypasses all permission checks
    if (isSuperAdmin) {
      return true;
    }

    if (!permissions?.length) return false;

    const perm = permissions.find(
      (p) =>
        p.section?.toLowerCase() === section.toLowerCase() &&
        p.permission_key?.toLowerCase() === key.toLowerCase()
    );

    if (!perm) return false;

    const actionMap = {
      read: "can_read",
      create: "can_create",
      update: "can_update",
      delete: "can_delete",
      access: "has_access",
    };

    const field = actionMap[action];
    return field ? Boolean(perm[field]) : false;
  }, [permissions, isSuperAdmin]);

  /**
   * Check if user is an organization admin (has Admin role or higher)
   */
  const isOrgAdmin = useMemo(() => () => {
    if (isSuperAdmin) return true;
    return role === ROLES.ORG_ADMIN || role === 'admin';
  }, [isSuperAdmin, role]);

  /**
   * Check if user has any of the specified actions for a permission
   */
  const canAny = useMemo(() => (section, key, actions) => {
    return actions.some(action => can(section, key, action));
  }, [can]);

  return (
    <PermissionContext.Provider value={{ 
      role, 
      permissions, 
      can, 
      canAny,
      loading, 
      hasOrganization,
      isSuperAdmin,
      isOrgAdmin,
      allOrganisations
    }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);
